import { Mail, Phone, Globe } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";

import { ShareArticleSection } from "../ShareArticleSection";

const IntelligentIVR = () => {
  return (
    <div className="modal-content-body">
      <ShareArticleSection />
      <div className="modal-section">
        <div className="flex-content">
          <div>
            <h3>Use Case</h3>
          </div>
          <div>
            <p>
              Transform legacy or basic IVR into a conversational, AI-powered
              entry point—enabling natural language, faster intent resolution,
              cognitive routing, and seamless digital self-service.
            </p>
          </div>
        </div>

        <div className="flex-content">
          <div>
            <h3>Solution</h3>
          </div>
          <div>
            <a
              href="https://aws.amazon.com/marketplace/pp/prodview-q3lp7ipqr66pa?sr=0-2&ref_=beagle&applicationId=AWSMPContessa"
              target="_blank"
              rel="noopener noreferrer"
            >
              Intelligent IVR Design
            </a>{" "}
            (Amazon Q, Lex, GenAI Orchestration)
          </div>
        </div>

        <div className="flex-content">
          <div>
            <h3>Target Segment</h3>
          </div>
          <div>
            <ul>
              <li>
                SMB/Enterprise with legacy IVR, complex call flows, or high
                customer frustration
              </li>
              <li>
                Organisations seeking higher containment, faster resolution, or
                a “GenAI” IVR refresh
              </li>
              <li>
                Head of CX/Contact Centre, IT Manager, Digital Transformation
                Lead, Ops Director, Product Owner
              </li>
            </ul>
          </div>
        </div>

        <div className="flex-content">
          <div>
            <h3>Target Personas</h3>
          </div>
          <div>
            <p>
              Head of CX/Contact Centre, IT Manager, Digital Transformation
              Lead, Ops Director, Product Owner
            </p>
          </div>
        </div>
      </div>

      {/* Opportunity & Qualification */}
      <div className="modal-section">
        <h2 className="modal-section-title">Opportunity and Qualification</h2>
        <div className="modal-section-grid">
          <div className="modal-column">
            <h3 className="modal-subtitle">
              What is the customer trying to accomplish?
            </h3>
            <ul>
              <li>
                Expedite customer intent capture with natural language and GenAI
              </li>
              <li>Reduce transfers, queue times, and agent workload</li>
              <li>Personalise and automate self-service workflows</li>
              <li>Integrate IVR with CRM and digital channels</li>
              <li>
                Unlock advanced analytics on call drivers and intent resolution
              </li>
            </ul>
          </div>

          <div className="modal-column">
            <h3 className="modal-subtitle">Key Words/ Questions</h3>
            <ul>
              <li>
                Frustration with current IVR (long menus, repeat transfers)
              </li>
              <li>Desire for natural language — “no more press 1 for…”</li>
              <li>Need for smarter routing and reduced queue times</li>
              <li>Wanting modern digital and voice self-service</li>
              <li>Facing high agent workload or hold times</li>
            </ul>
          </div>

          <div className="modal-column">
            <h3 className="modal-subtitle">Pain Point/ Challenge</h3>
            <ul>
              <li>High abandonment and call transfer rates</li>
              <li>Poor customer satisfaction with “press button” IVR</li>
              <li>Limited self-service and high manual workload</li>
              <li>
                Inability to promote digital channels or alternatives (SMS,
                chat)
              </li>
              <li>
                Lack of visibility on customer needs and real-time IVR analytics
              </li>
            </ul>
          </div>

          <div className="modal-column">
            <h3 className="modal-subtitle">Qualifying Questions</h3>
            <ul>
              <li>
                Are customers frequently dropped, transferred, or lost in your
                IVR?
              </li>
              <li>
                Do your agents spend significant time on simple, repeatable
                queries?
              </li>
              <li>
                Would modernising to conversational AI reduce operational cost
                and boost CX?
              </li>
              <li>
                Are you interested in using NLP, biometrics, or intent-based
                routing?
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Proposed Solution */}
      <div className="modal-section">
        <h2 className="modal-section-title">Proposed Solution / Value</h2>
        <div className="flex-content-half">
          <div>
            <h3>Solution Description</h3>
            <ul style={{ marginTop: "10px" }}>
              <li>
                Continuous Amazon Connect improvement via regular consult
                reviews, audits, and innovation sessions. Let us do the grunt
                work.
              </li>
              <li>
                Embeds advanced analytics, AI/GenAI, knowledge management, and
                workflow automation
              </li>
              <li>
                24/7 monitoring, incident response, compliance, and cost
                optimisation
              </li>
              <li>
                Expert support for roadmap planning and business case delivery
              </li>
            </ul>
          </div>
          <div>
            <h3>Solution Benefits to AWS Customers</h3>
            <ul style={{ marginTop: "10px" }}>
              <li>40%+ CX efficiency gains, measurable and ongoing</li>
              <li>Always-current AWS features unleash new value</li>
              <li>Reduced risk, lower TCO, and proactive compliance</li>
              <li>Data-driven improvement for stronger retention and NPS</li>
            </ul>
          </div>
        </div>

        <h2 className="modal-section-title" style={{ marginTop: "30px" }}>
          Objection Handling
        </h2>
        <div className="flex-content-half">
          <div>
            <p>
              <b>IVR Upgrades Are Complex:</b> Delivered as a fully managed,
              turn-key service with minimal IT lift.
            </p>

            <p>
              <strong>Our Customers Won't Use Self-Service:</strong>
              Conversational AI raises containment and satisfaction, with
              seamless escalation
            </p>
            <p>
              <strong>We're Not Ready for GenAI:</strong> Modular design - AI
              and GenAI features can be activated as business is ready.
            </p>
            <p>
              <strong>Integration Seems Risky:</strong> Deep AWS and CRM
              integration expertise ensures robust, secure workflows.
            </p>
            <p>
              <strong>ROI Unclear:</strong> Results: 30% fewer transfers, faster
              intent capture, real-time analytics, and proven client wins.
            </p>
          </div>
        </div>
      </div>

      <div className="modal-footnote" style={{ border: 0 }}>
        <h3>Primary Partner Contact:</h3>
        <div className="flex-box-blk">
          <div>
            <img
              src="https://www.icxeed.ai/wp-content/themes/icxeed-website-2025/portal-assets/images/thumbnails/ryan-thumbnail.png"
              alt="ryan rayner"
            />
          </div>
          <div>
            <h2>Ryan Rayner</h2>
            <p>Co-Founder & Chief Customer Officer</p>
          </div>
        </div>
        <div className="flex-box-blk footnote-flex-box">
          <div className="flex-box-blk">
            <div>
              <Mail size={20} color="#01C2F3" />
            </div>
            <div>
              <a href="mailto:ryan.rayner@icxeed.ai">ryan.rayner@icxeed.ai</a>
            </div>
          </div>
          <div className="flex-box-blk">
            <div>
              <Phone size={20} color="#01C2F3" />
            </div>
            <div>
              <a href="tel:+61438286198">+61 438 286 198</a>
            </div>
          </div>
        </div>

        <div className="flex-box-blk footnote-flex-box">
          <div className="flex-box-blk">
            <div>
              <FontAwesomeIcon icon={faLinkedinIn} size="lg" color="#01C2F3" />
            </div>
            <div>
              <a
                href=" https://www.linkedin.com/in/ryanrayner/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.linkedin.com/in/ryanrayner/
              </a>
            </div>
          </div>
          <div className="flex-box-blk">
            <div>
              <Globe size={20} color="#01C2F3" />
            </div>
            <div>
              <a
                href="https://icxeed.ai/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://icxeed.ai/
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntelligentIVR;
